import React from 'react'
import moment from 'moment'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import {
  TaskTemplatePriorityType,
  TaskTemplateProperties,
} from '@core/entities/task/TaskTemplateProperties'
import { Task } from '@core/entities/task/Task'
import { useModal } from 'webapp/components/common/GlobalModal'
import { useToast } from '@core/toast'
import { useEditTask } from '@core/react-query/features/tasks/task'
import { localizedStrings } from '@core/strings'
import {
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  TextInput,
} from '@common/material'
import { DatePicker } from '@common/material/DatePicker'
import capitalize from 'lodash/capitalize'
import { Box, Flex, Text } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import { MAX_TASK_SUMMARY_CHARACTERS } from '@core/components/templateAnswersForm/templateRules'
import ClearIcon from '@mui/icons-material/Clear'

interface ComponentType {
  task: Task
}

type FormData = {
  summary: string
  priority: TaskTemplatePriorityType
  dueDate: string
}

export const EditTaskModal: React.FC<ComponentType> = ({ task }) => {
  const { hideModal } = useModal()
  const toast = useToast()
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      summary: task?.summary || '',
      priority: task?.priority || '',
      dueDate: task?.dueDate || '',
    },
  })

  const editTaskMutation = useEditTask()

  const onSubmit: SubmitHandler<FormData> = (data) => {
    editTaskMutation.mutate(
      {
        id: task.id,
        summary: data.summary,
        priority: data.priority,
        dueDate: data.dueDate,
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.taskEditedSuccessfully)
        },
        onError: (error) => {
          toast.error(error.message)
        },
        onSettled: () => {
          hideModal()
        },
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.editTask}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection={'column'}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name='summary'
              control={control}
              rules={{ required: localizedStrings.pleaseEnterADescription }}
              render={({ field: { onChange, ...field }, fieldState }) => {
                return (
                  <TextField
                    label={localizedStrings.description}
                    placeholder={localizedStrings.description}
                    data-test='task-summary-input'
                    sx={{ mt: 2 }}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      `${
                        field.value?.length || 0
                      }/${MAX_TASK_SUMMARY_CHARACTERS} characters`
                    }
                    onChange={(e) =>
                      e.target.value.length <= MAX_TASK_SUMMARY_CHARACTERS &&
                      onChange(e)
                    }
                    slotProps={{
                      formHelperText: {
                        error: !!fieldState.error,
                        sx: { pt: 0.5, pb: 2 },
                      },
                    }}
                    fullWidth
                    required
                    {...field}
                  />
                )
              }}
            />

            <Controller
              name='priority'
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <TextInput
                    sx={{ mt: 3 }}
                    label={localizedStrings.priority}
                    select
                    size='small'
                    data-test='task-priority-dropdown-input'
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        sx: {
                          display: 'flex',
                          alignItems: 'center',
                          p: 2,
                        },
                      },
                      inputLabel: {
                        shrink: Boolean(field.value), // Automatically handle shrink based on value or focus
                        sx: {
                          marginTop: 1, // Default margin
                          '&.MuiInputLabel-shrink': {
                            marginTop: 0, // Remove margin when label is shrunk (focused or has value)
                          },
                        },
                      },
                    }}
                    {...field}
                    onChange={(e) => {
                      const priority = e.target
                        .value as TaskTemplatePriorityType
                      field.onChange(priority)

                      const dueDateConfig =
                        task?.template?.currentVersion?.properties
                          ?.dueDateConfig

                      if (dueDateConfig?.prioritySetsTheDueDate) {
                        const dueDate = Task.calculateDueDate({
                          priority,
                          dueDateConfig,
                        })
                        setValue('dueDate', dueDate)
                      }
                    }}
                  >
                    {task?.template?.currentVersion?.properties
                      ?.getPriorityOptions()
                      ?.map((option) => (
                        <MenuItem
                          key={option.priority}
                          value={option.priority}
                          sx={{ display: 'flex', alignItems: 'center' }}
                          data-test={`task-priority-dropdown-option-${option.priority}`}
                        >
                          <Box
                            height={16}
                            width={16}
                            borderRadius='50%'
                            sx={{
                              backgroundColor:
                                TaskTemplateProperties.getColorForPriority(
                                  option.priority,
                                ),
                              mr: 1,
                            }}
                          />
                          <Text variant='body1'>
                            {capitalize(option?.priority)}
                          </Text>
                        </MenuItem>
                      ))}
                  </TextInput>
                )
              }}
            />

            <Controller
              name='dueDate'
              control={control}
              rules={{
                required:
                  task?.template?.currentVersion?.properties?.getDueDateRequired(),
              }}
              render={({
                field: { value, onChange, ...field },
                fieldState,
              }) => {
                return (
                  <DatePicker
                    dataTest='date-picker-due-date'
                    value={value ? moment(value) : undefined}
                    onChange={(rawDate) => {
                      if (!rawDate) return
                      const dueDate = Task.calculateDueDate({
                        rawDate,
                        priority: task?.priority,
                        dueDateConfig:
                          task?.template?.currentVersion?.properties
                            ?.dueDateConfig,
                      })
                      onChange(dueDate)
                    }}
                    withTime={true}
                    label={localizedStrings.dueDate}
                    error={!!fieldState.error}
                    sx={{ flex: 1, mt: 3, width: '100%' }}
                    required={task?.template?.currentVersion?.properties?.getDueDateRequired()}
                    disabled={
                      task?.template?.currentVersion?.properties?.getDueDatePrioritySetsTheDueDate() &&
                      !task?.template?.currentVersion?.properties?.dueDateConfig
                        ?.prioritySetsTheDueDateConfig
                        ?.allowDefaultsToBeOverridden
                    }
                    {...field}
                  />
                )
              }}
            />

            <Flex gap={2} mt={4}>
              <Button variant='outlined' onClick={hideModal} fullWidth>
                {localizedStrings.cancel}
              </Button>
              <Button
                variant='contained'
                type='submit'
                fullWidth
                data-test='save-task'
                disabled={editTaskMutation.isLoading}
              >
                {localizedStrings.save}
              </Button>
            </Flex>
          </form>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
